<template>
  <div data-testid="payment-form" class="max-w-2xl mx-auto">
    <!-- Order Amount Summary -->
    <div class="border border-gray-200 p-6 mb-6">
      <h3 class="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">{{ $t('booking.step_4.payment_summary') }}</h3>

      <!-- Total Package Price -->
      <div class="flex justify-between text-sm text-gray-600 mb-2">
        <span>{{ $t('booking.step_4.total_package_price') }}</span>
        <span>฿{{ formatAmount(totalPackagePrice) }}</span>
      </div>

      <!-- Deposit Amount -->
      <div class="flex justify-between text-sm text-gray-600 mb-2">
        <span>{{ $t('booking.step_4.deposit_due_now') }}</span>
        <span>฿{{ formatAmount(depositAmount) }}</span>
      </div>

      <!-- Total Payment -->
      <div class="flex justify-between text-gray-900 font-medium text-base border-t border-gray-200 pt-3 mt-3">
        <span>{{ $t('booking.step_4.total_payment') }}</span>
        <span>฿{{ formatAmount(totalPaymentAmount) }}</span>
      </div>

      <!-- Balance Note -->
      <p class="text-xs text-gray-600 mt-3 pt-3 border-t border-gray-100">
        {{ $t('booking.step_4.balance_note', {balance: formatAmount(balanceDue)}) }}
      </p>
    </div>

    <!-- Payment Success State -->
    <div v-if="paymentSuccess" data-testid="payment-success" class="border border-gray-200 p-8 text-center">
      <div class="w-16 h-16 mx-auto mb-4 bg-gray-900 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </div>
      <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">{{ $t('booking.step_4.payment_success.confirmed') }}</p>
      <h3 class="text-xl font-light text-gray-900 mb-2">{{ $t('booking.step_4.payment_success.title') }}</h3>
      <p class="text-gray-600 text-sm mb-1">{{ $t('booking.step_4.payment_success.order_confirmed', {orderId: order.id}) }}</p>
      <p class="text-gray-500 text-xs">{{ $t('booking.step_4.payment_success.redirecting') }}</p>
    </div>

    <!-- Payment Methods Tabs -->
    <div v-else-if="!checkingStatus && !checkStatusExhausted" class="border border-gray-200">
      <!-- Tab Headers -->
      <div class="flex border-b border-gray-200">
        <button
          @click="setPaymentMethod('card')"
          data-testid="payment-tab-card"
          :disabled="isMethodLocked('card')"
          :class="[
            'flex-1 px-6 py-4 text-sm font-medium tracking-wide transition-colors',
            isMethodLocked('card') ? 'opacity-40 cursor-not-allowed' : '',
            activeTab === 'card'
              ? 'text-gray-900 border-b-2 border-gray-900 bg-white'
              : 'text-gray-500 hover:text-gray-700 bg-stone-50'
          ]"
        >
          {{ $t('booking.step_4.payment_methods.card') }}
        </button>
        <button
          @click="setPaymentMethod('promptpay')"
          data-testid="payment-tab-promptpay"
          :disabled="isMethodLocked('promptpay')"
          :class="[
            'flex-1 px-6 py-4 text-sm font-medium tracking-wide transition-colors',
            isMethodLocked('promptpay') ? 'opacity-40 cursor-not-allowed' : '',
            activeTab === 'promptpay'
              ? 'text-gray-900 border-b-2 border-gray-900 bg-white'
              : 'text-gray-500 hover:text-gray-700 bg-stone-50'
          ]"
        >
          {{ $t('booking.step_4.payment_methods.promptpay') }}
        </button>
      </div>

      <div v-if="activePaymentMethod" class="border-b border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900">
        {{ $t('booking.step_4.payment_in_progress', { method: activePaymentLabel }) }}
      </div>

      <!-- Card Tab -->
      <div v-if="activeTab === 'card'" class="p-6">
        <div v-if="cardAuthorizeUrl" class="text-center py-5">
          <p class="text-sm text-gray-600 mb-5">{{ $t('booking.step_4.card.continue_3ds') }}</p>
          <button
            @click="redirectToCardAuthorization"
            class="inline-flex items-center justify-center px-8 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800"
          >
            {{ $t('booking.step_4.card.continue_3ds_button') }}
          </button>
        </div>
        <div v-else class="space-y-4">
          <!-- Card Number -->
          <div>
            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              {{ $t('booking.step_4.card.card_number') }}
            </label>
            <input
              v-model="card.number"
              data-testid="card-number"
              type="text"
              inputmode="numeric"
              placeholder="1234 5678 9012 3456"
              maxlength="19"
              @input="formatCardNumber"
              class="w-full px-3 py-2.5 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm tracking-wider"
            />
          </div>

          <!-- Expiry + CVV -->
          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_4.card.month') }}
              </label>
              <input
                v-model="card.expiry_month"
                data-testid="card-expiry-month"
                type="text"
                inputmode="numeric"
                placeholder="MM"
                maxlength="2"
                class="w-full px-3 py-2.5 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm text-center"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_4.card.year') }}
              </label>
              <input
                v-model="card.expiry_year"
                data-testid="card-expiry-year"
                type="text"
                inputmode="numeric"
                placeholder="YYYY"
                maxlength="4"
                class="w-full px-3 py-2.5 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm text-center"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_4.card.cvv') }}
              </label>
              <input
                v-model="card.security_code"
                data-testid="card-cvv"
                type="text"
                inputmode="numeric"
                placeholder="123"
                maxlength="4"
                class="w-full px-3 py-2.5 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm text-center"
              />
            </div>
          </div>

          <!-- Name on Card -->
          <div>
            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              {{ $t('booking.step_4.card.name_on_card') }}
            </label>
            <input
              v-model="card.name"
              data-testid="card-name"
              type="text"
              placeholder="As shown on card"
              class="w-full px-3 py-2.5 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
            />
          </div>

          <!-- Error -->
          <div v-if="cardError" class="bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {{ cardError }}
          </div>

          <!-- Pay Button -->
          <button
            @click="payWithCard"
            :disabled="cardSubmitting || !isCardValid"
            data-testid="pay-by-card"
            class="w-full py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="cardSubmitting" class="flex items-center justify-center gap-2">
              <span class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
              {{ $t('booking.step_4.card.processing') }}
            </span>
            <span v-else>{{ $t('booking.step_4.card.pay', {amount: formatAmount(totalPaymentAmount)}) }}</span>
          </button>

          <p class="text-xs text-gray-400 text-center">
            {{ $t('booking.step_4.card.secured_by') }}
          </p>
        </div>
      </div>

      <!-- PromptPay Tab -->
      <div v-if="activeTab === 'promptpay'" class="p-6">
        <!-- Before QR generated -->
        <div v-if="!promptpayQrUrl && !promptpayError" class="text-center py-4">
          <p class="text-gray-600 text-sm mb-6">
            {{ $t('booking.step_4.promptpay.scan_desc', {amount: formatAmount(totalPaymentAmount)}) }}
          </p>
          <button
            @click="generatePromptPay"
            :disabled="promptpayLoading"
            data-testid="generate-promptpay"
            class="inline-flex items-center justify-center px-8 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <span v-if="promptpayLoading" class="flex items-center gap-2">
              <span class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
              {{ $t('booking.step_4.promptpay.generating') }}
            </span>
            <span v-else>{{ $t('booking.step_4.promptpay.generate_qr') }}</span>
          </button>
        </div>

        <!-- QR Code Display -->
        <div v-else-if="promptpayQrUrl" class="text-center">
          <p class="text-sm text-gray-600 mb-4">
            {{ $t('booking.step_4.promptpay.scan_instruction') }}
          </p>
          <div class="inline-block border border-gray-200 p-4 mb-4 bg-white">
            <img :src="promptpayQrUrl" alt="PromptPay QR Code" data-testid="promptpay-qr" class="w-52 h-52 mx-auto" />
          </div>
          <p class="text-xl font-medium text-gray-900 mb-1">฿{{ formatAmount(totalPaymentAmount) }}</p>

          <!-- Polling status -->
          <div class="mt-4">
            <div v-if="promptpayPolling" class="flex items-center justify-center gap-2 text-sm text-gray-500">
              <span class="animate-spin inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full"></span>
              {{ $t('booking.step_4.promptpay.waiting') }}
            </div>
            <p class="text-xs text-gray-400 mt-2">{{ $t('booking.step_4.promptpay.expires_in', {time: expiryCountdown}) }}</p>
          </div>
        </div>

        <!-- Error -->
        <div v-if="promptpayError" class="bg-red-50 border border-red-200 p-3 text-sm text-red-700 mt-4">
          {{ promptpayError }}
        </div>
      </div>
    </div>

    <!-- Checking status (after 3DS return) -->
    <div v-else-if="checkingStatus" class="border border-gray-200 p-8 text-center">
      <div class="animate-spin w-10 h-10 border-2 border-gray-900 border-t-transparent rounded-full mx-auto mb-4"></div>
      <p class="text-gray-600 text-sm">{{ $t('booking.step_4.checking_status') }}</p>
    </div>

    <!-- Status check exhausted — webhook hasn't arrived yet, offer retry -->
    <div v-else-if="checkStatusExhausted" class="border border-gray-200 p-8 text-center">
      <p class="text-gray-700 text-sm mb-4">
        {{ $t('booking.step_4.status_delayed') }}
      </p>
      <button
        @click="retryStatusCheck"
        class="inline-flex items-center justify-center px-6 py-2.5 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
      >
        {{ $t('booking.step_4.retry_status') }}
      </button>
      <p v-if="cardError" class="text-xs text-red-600 mt-3">{{ cardError }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
type PaymentMethod = 'card' | 'promptpay'

interface PaymentNextAction {
  type: 'redirect' | 'promptpay_qr'
  url: string
  expires_at?: string | null
}

interface PaymentStatusResponse {
  payment_status?: string
  order_status?: string
  charge_status?: string | null
  payment_method?: PaymentMethod | null
  next_action?: PaymentNextAction | null
  status?: string
  charge_id?: string
  qr_code_url?: string
  authorize_uri?: string
  expires_at?: string | null
  code?: string
  error?: string
}

interface Order {
  id: number | string
  total_deposit: string
  total_price: string
  access_token?: string
  booking_date?: string
  studio_slot_time?: string
  photo_slot_time?: string
}

const props = defineProps<{
  order: Order
  isReturningFrom3ds?: boolean
}>()

const emit = defineEmits<{
  (e: 'payment-success'): void
  (e: 'payment-failed', message: string): void
}>()

const API_BASE_URL = '/api/v1'

// Storage key shared with BookingView for surviving 3DS round-trip.
// sessionStorage scopes to the tab and is wiped on close — preferred over
// localStorage which would leak abandoned payments across visits.
const PENDING_PAYMENT_KEY = 'pending_payment_order'

// Build query string with access_token attached (required for guest orders)
function withAccessToken(): string {
  const token = props.order.access_token
  return token ? `?access_token=${encodeURIComponent(token)}` : ''
}

const activeTab = ref<PaymentMethod>('card')
const activePaymentMethod = ref<PaymentMethod | null>(null)
const paymentSuccess = ref(false)
const checkingStatus = ref(false)

// Card state
const card = ref({
  number: '',
  expiry_month: '',
  expiry_year: '',
  security_code: '',
  name: ''
})
const cardError = ref('')
const cardSubmitting = ref(false)
const cardAuthorizeUrl = ref('')

// PromptPay state
const promptpayQrUrl = ref('')
const promptpayChargeId = ref('')
const promptpayLoading = ref(false)
const promptpayPolling = ref(false)
const promptpayError = ref('')
const expiryCountdown = ref('15:00')

let pollInterval: ReturnType<typeof setInterval> | null = null
let countdownInterval: ReturnType<typeof setInterval> | null = null
let expirySeconds = 15 * 60

// Luhn checksum — catches typos before round-tripping to Omise
function luhnValid(num: string): boolean {
  if (num.length < 13 || num.length > 19) return false
  let sum = 0
  let alt = false
  for (let i = num.length - 1; i >= 0; i--) {
    let n = parseInt(num[i], 10)
    if (Number.isNaN(n)) return false
    if (alt) {
      n *= 2
      if (n > 9) n -= 9
    }
    sum += n
    alt = !alt
  }
  return sum % 10 === 0
}

function expiryInFuture(monthStr: string, yearStr: string): boolean {
  const month = parseInt(monthStr, 10)
  const year = parseInt(yearStr, 10)
  if (Number.isNaN(month) || Number.isNaN(year)) return false
  if (month < 1 || month > 12) return false
  // Card is valid through end of expiry month
  const now = new Date()
  const expiryEnd = new Date(year, month, 1) // first day of month AFTER expiry
  return expiryEnd > now
}

// Computed values
const isCardValid = computed(() => {
  const num = card.value.number.replace(/\s/g, '')
  return (
    luhnValid(num) &&
    card.value.expiry_month.length === 2 &&
    card.value.expiry_year.length === 4 &&
    expiryInFuture(card.value.expiry_month, card.value.expiry_year) &&
    card.value.security_code.length >= 3 &&
    card.value.name.trim().length > 0
  )
})

const balanceDue = computed(() => {
  const total = parseFloat(props.order.total_price) || 0
  const deposit = parseFloat(props.order.total_deposit) || 0
  return Math.max(0, total - deposit)
})

const depositAmount = computed(() => {
  return parseFloat(props.order.total_deposit) || 0
})

const totalPackagePrice = computed(() => {
  return parseFloat(props.order.total_price) || 0
})

const totalPaymentAmount = computed(() => {
  return depositAmount.value
})

const activePaymentLabel = computed(() => {
  return activePaymentMethod.value === 'promptpay' ? 'PromptPay' : 'Credit / Debit Card'
})

// Helper functions
function formatAmount(amount: number): string {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}

function setPaymentMethod(method: PaymentMethod) {
  if (isMethodLocked(method)) return

  activeTab.value = method
  // Reset PromptPay state when switching methods
  if (method === 'card') {
    promptpayQrUrl.value = ''
    promptpayError.value = ''
    stopPolling()
  }
}

function isMethodLocked(method: PaymentMethod): boolean {
  return activePaymentMethod.value !== null && activePaymentMethod.value !== method
}

function redirectToCardAuthorization() {
  if (cardAuthorizeUrl.value) window.location.href = cardAuthorizeUrl.value
}

async function responseJson(res: Response): Promise<PaymentStatusResponse> {
  const data = await res.json() as PaymentStatusResponse
  if (!res.ok) {
    const error = new Error(data.error || 'Payment service is temporarily unavailable')
    Object.assign(error, { paymentData: data })
    throw error
  }
  return data
}

function handlePaymentConflict(error: unknown): boolean {
  const data = (error as Error & { paymentData?: PaymentStatusResponse }).paymentData
  if (data?.code !== 'payment_in_progress' || !data.payment_method) return false

  resumePendingPayment(data.payment_method, data.next_action || null)
  return true
}

function resumePendingPayment(method: PaymentMethod, action: PaymentNextAction | null) {
  activePaymentMethod.value = method
  activeTab.value = method

  if (method === 'promptpay' && action?.type === 'promptpay_qr' && action.url) {
    promptpayQrUrl.value = action.url
    promptpayError.value = ''
    startPromptPayPolling()
    startCountdown(action.expires_at)
  } else if (method === 'card' && action?.type === 'redirect' && action.url) {
    cardAuthorizeUrl.value = action.url
  }
}

function markPaymentSuccess() {
  stopPolling()
  paymentSuccess.value = true
  setTimeout(() => emit('payment-success'), 2000)
}

function applyPaymentStatus(data: PaymentStatusResponse): boolean {
  if (data.payment_status === 'paid') {
    if (data.order_status && data.order_status !== 'confirmed') {
      const message = 'Payment was received but the booking needs assistance. Please contact support.'
      cardError.value = message
      emit('payment-failed', message)
      return true
    }

    markPaymentSuccess()
    return true
  }

  if (data.payment_status === 'failed' || data.charge_status === 'expired') {
    stopPolling()
    activePaymentMethod.value = null
    cardAuthorizeUrl.value = ''
    promptpayQrUrl.value = ''
    return true
  }

  if (data.charge_status === 'pending' && data.payment_method) {
    resumePendingPayment(data.payment_method, data.next_action || null)
  }

  return false
}

async function loadPaymentStatus() {
  checkingStatus.value = true
  try {
    const res = await fetch(
      `${API_BASE_URL}/orders/${props.order.id}/payment_status${withAccessToken()}`,
      { credentials: 'include' }
    )
    const data = await responseJson(res)
    applyPaymentStatus(data)
  } catch (err) {
    cardError.value = err instanceof Error ? err.message : 'Could not load payment status.'
  } finally {
    checkingStatus.value = false
  }
}

function formatCardNumber(e: Event) {
  const input = e.target as HTMLInputElement
  let val = input.value.replace(/\D/g, '').slice(0, 16)
  card.value.number = val.replace(/(.{4})/g, '$1 ').trim()
}

// --- Card Payment ---

function clearCardData() {
  card.value = {
    number: '',
    expiry_month: '',
    expiry_year: '',
    security_code: '',
    name: ''
  }
}

async function payWithCard() {
  cardError.value = ''
  cardSubmitting.value = true

  try {
    const omise = (window as any).Omise
    if (!omise) throw new Error('Payment library not loaded. Please refresh the page.')

    const publicKey = document
      .querySelector<HTMLMetaElement>('meta[name="omise-public-key"]')
      ?.content.trim()
    if (!publicKey) throw new Error('Payment configuration is unavailable. Please contact support.')
    omise.setPublicKey(publicKey)

    const rawNumber = card.value.number.replace(/\s/g, '')

    const token = await new Promise<string>((resolve, reject) => {
      omise.createToken('card', {
        name: card.value.name,
        number: rawNumber,
        expiration_month: parseInt(card.value.expiry_month),
        expiration_year: parseInt(card.value.expiry_year),
        security_code: card.value.security_code
      }, (statusCode: number, response: any) => {
        if (statusCode === 200) {
          resolve(response.id)
        } else {
          reject(new Error(response.message || 'Card tokenization failed'))
        }
      })
    })

    const res = await fetch(`${API_BASE_URL}/orders/${props.order.id}/charge${withAccessToken()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        token,
        payment_method: 'card'
      })
    })

    const data = await responseJson(res)

    if (data.status === 'successful' || data.status === 'already_paid') {
      clearCardData()
      markPaymentSuccess()
    } else if (data.status === 'pending' && data.authorize_uri) {
      // 3DS redirect — save order info to sessionStorage before leaving
      // Persist the whole order — the booking date/times are needed to render
      // the thank-you page after the redirect, where the in-memory cart state
      // no longer exists.
      sessionStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify({ ...props.order }))
      activePaymentMethod.value = 'card'
      clearCardData()
      window.location.href = data.authorize_uri
    } else {
      throw new Error('Unexpected payment response. Please try again.')
    }
  } catch (err) {
    if (handlePaymentConflict(err)) return

    const message = err instanceof Error ? err.message : 'Payment failed. Please try again.'
    cardError.value = message
    emit('payment-failed', message)
  } finally {
    cardSubmitting.value = false
  }
}

// --- PromptPay ---

async function generatePromptPay() {
  promptpayError.value = ''
  promptpayLoading.value = true

  try {
    const res = await fetch(`${API_BASE_URL}/orders/${props.order.id}/promptpay${withAccessToken()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        payment_method: 'promptpay'
      })
    })

    const data = await responseJson(res)

    if (data.status === 'already_paid') {
      markPaymentSuccess()
      return
    }

    if (!data.qr_code_url) throw new Error('The payment gateway did not return a QR code.')

    activePaymentMethod.value = 'promptpay'
    promptpayQrUrl.value = data.qr_code_url
    promptpayChargeId.value = data.charge_id || ''
    startPromptPayPolling()
    startCountdown(data.expires_at)
  } catch (err) {
    if (handlePaymentConflict(err)) return

    const message = err instanceof Error ? err.message : 'Failed to generate QR code'
    promptpayError.value = message
    emit('payment-failed', message)
  } finally {
    promptpayLoading.value = false
  }
}

// Stop polling while tab is hidden — saves backend traffic for forgotten tabs.
// Resume on visibility return.
function handleVisibilityChange() {
  if (!promptpayQrUrl.value) return
  if (document.hidden) {
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }
  } else if (!pollInterval && promptpayPolling.value) {
    pollInterval = setInterval(pollPromptPayOnce, 5000)
  }
}

async function pollPromptPayOnce() {
  try {
    const res = await fetch(
      `${API_BASE_URL}/orders/${props.order.id}/payment_status${withAccessToken()}`,
      { credentials: 'include' }
    )
    const data = await responseJson(res)

    if (applyPaymentStatus(data) && data.payment_status === 'failed') {
      const message = 'Payment failed or expired. Please try again.'
      promptpayError.value = message
      emit('payment-failed', message)
    }
  } catch {
    // Network error — keep polling; countdown will eventually expire
  }
}

function startPromptPayPolling() {
  if (pollInterval) clearInterval(pollInterval)
  promptpayPolling.value = true
  pollInterval = setInterval(pollPromptPayOnce, 5000)
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

function startCountdown(expiresAt?: string | null) {
  if (countdownInterval) clearInterval(countdownInterval)
  const parsedExpiry = expiresAt ? Date.parse(expiresAt) : Number.NaN
  expirySeconds = Number.isNaN(parsedExpiry)
    ? 15 * 60
    : Math.max(0, Math.ceil((parsedExpiry - Date.now()) / 1000))
  updateExpiryCountdown()
  countdownInterval = setInterval(() => {
    expirySeconds = Math.max(0, expirySeconds - 1)
    updateExpiryCountdown()
    if (expirySeconds <= 0) {
      if (countdownInterval) clearInterval(countdownInterval)
      countdownInterval = null
      pollPromptPayOnce()
    }
  }, 1000)
}

function updateExpiryCountdown() {
  const mins = Math.floor(expirySeconds / 60)
  const secs = expirySeconds % 60
  expiryCountdown.value = `${mins}:${String(secs).padStart(2, '0')}`
}

function stopPolling() {
  promptpayPolling.value = false
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

// --- 3DS Return Handling ---

// More attempts (15 × 2s = 30s) than the original 12s — webhooks can lag.
// On exhaustion, surface a retry button instead of silently dropping the user.
const checkStatusExhausted = ref(false)
const POST_3DS_MAX_ATTEMPTS = 15
const POST_3DS_INTERVAL_MS = 2000

async function checkStatusAfter3ds() {
  checkingStatus.value = true
  checkStatusExhausted.value = false

  try {
    for (let i = 0; i < POST_3DS_MAX_ATTEMPTS; i++) {
      await new Promise(r => setTimeout(r, POST_3DS_INTERVAL_MS))
      const res = await fetch(
        `${API_BASE_URL}/orders/${props.order.id}/payment_status${withAccessToken()}`,
        { credentials: 'include' }
      )
      const data = await responseJson(res)

      if (data.payment_status === 'paid' && data.order_status === 'confirmed') {
        markPaymentSuccess()
        return
      } else if (data.payment_status === 'failed') {
        const message = 'Payment was not completed. Please try again.'
        cardError.value = message
        emit('payment-failed', message)
        return
      }
    }
    // Webhook still not in. Show retry UI rather than dropping silently.
    checkStatusExhausted.value = true
  } catch {
    checkStatusExhausted.value = true
    cardError.value = 'Could not verify payment status. Please retry or contact support.'
  } finally {
    checkingStatus.value = false
  }
}

function retryStatusCheck() {
  checkStatusAfter3ds()
}

onMounted(() => {
  if (props.isReturningFrom3ds) {
    checkStatusAfter3ds()
  } else {
    loadPaymentStatus()
  }
})

onUnmounted(() => {
  stopPolling()
})
</script>
